import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { TopBar } from './TopBar';
import { EngineRail } from './EngineRail';
import { CapabilityNav } from './CapabilityNav';
import { CopilotRail } from './CopilotRail';
import { CommandPalette } from './CommandPalette';
import { MobileDrawer } from './MobileDrawer';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { DomainSelector } from '@/components/DomainSelector';
import { engineForPath } from '@/engines/registry';
import { EngineThemeProvider } from '@/theme/EngineThemeProvider';
import { HandsFreeProvider } from '@/voice/wake/HandsFreeProvider';

/* Read once at module load: is this a desktop viewport? Used only to choose the
 * copilot's initial open state (open on desktop as before; closed on mobile so
 * the bottom sheet doesn't cover a fresh page). Resizing is handled live by the
 * shell's breakpoint-scoped CSS. */
const DESKTOP_INITIAL =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(min-width: 1024px)').matches
    : true;

/* ----------------------------------------------------------------------------
 * The persistent three-zone shell (brief §5.1). Never fully unmounts:
 *   [ engine rail ][ capability nav ][ main outlet ][ copilot rail ]
 * with the top bar spanning the content column. On desktop (>= lg) this is
 * unchanged. Below lg the engine rail + capability nav collapse into an
 * accessible MobileDrawer (hamburger in the top bar) and the copilot rail
 * becomes a bottom sheet; the main outlet takes the full viewport width.
 * -------------------------------------------------------------------------- */
export function AppShell() {
  const location = useLocation();
  const engine = engineForPath(location.pathname);
  const [copilotOpen, setCopilotOpen] = useState(DESKTOP_INITIAL);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [copilotSeed, setCopilotSeed] = useState<string | undefined>();

  // Cmd/Ctrl-K opens the palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close the mobile nav drawer whenever the route changes.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  return (
    <HandsFreeProvider>
    <EngineThemeProvider engineKey={engine?.key} className="flex h-full min-h-0 bg-canvas">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-text-primary focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ai"
      >
        Skip to content
      </a>
      <EngineRail />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          onOpenPalette={() => setPaletteOpen(true)}
          onToggleCopilot={() => setCopilotOpen((o) => !o)}
          onOpenNav={() => setNavOpen(true)}
          copilotOpen={copilotOpen}
        />

        <div className="flex min-h-0 flex-1">
          {engine && engine.capabilities.length > 0 && <CapabilityNav engine={engine} />}

          <main id="main-content" tabIndex={-1} className="app-main min-w-0 flex-1 overflow-y-auto focus:outline-none">
            <div className="mx-auto max-w-6xl px-4 py-4 lg:px-6 lg:py-6">
              <Outlet />
            </div>
          </main>

          <CopilotRail
            open={copilotOpen}
            onToggle={() => setCopilotOpen((o) => !o)}
            seededQuery={copilotSeed}
          />
        </div>
      </div>

      {/* Mobile navigation drawer (< lg only) — workspace switch + engine rail +
          active-engine capabilities. Never mounts on desktop. */}
      <MobileDrawer open={navOpen} onClose={() => setNavOpen(false)}>
        <div className="border-b border-border-subtle p-2">
          <WorkspaceSwitcher />
        </div>
        {/* active-domain selector - drawer variant (Phase 2, Lane 2A).
            Only mounts here on mobile; the TopBar hosts the desktop one. */}
        <div className="border-b border-border-subtle">
          <DomainSelector variant="drawer" />
        </div>
        <EngineRail variant="drawer" onNavigate={() => setNavOpen(false)} />
        {engine && engine.capabilities.length > 0 && (
          <div className="border-t border-border-subtle">
            <CapabilityNav engine={engine} variant="drawer" onNavigate={() => setNavOpen(false)} />
          </div>
        )}
      </MobileDrawer>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onCopilot={(q) => {
          setCopilotSeed(q);
          setCopilotOpen(true);
        }}
      />
    </EngineThemeProvider>
    </HandsFreeProvider>
  );
}
